import React, { useState, useEffect } from "react";
import {
  Table, Button, Card, Row, Col, Select, Modal, Form, Input, InputNumber, message, Popconfirm, Space, Tooltip, Upload, Alert,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { Api_Url } from "../../../config/config";
import axios from "axios";
import { ErrorToast, SuccessToast } from "../../ToastComponent";

const { Option } = Select;

const ROLE_OPTIONS = [
  { value: 1, label: "Credit" },
  { value: 2, label: "Central Operations" },
  { value: 3, label: "Operations" },
];
const TYPE_OPTIONS = [
  { value: 1, label: "Branch" },
  { value: 2, label: "Employee" },
];
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const currentYear = new Date().getFullYear();
const YEARS = [currentYear - 1, currentYear, currentYear + 1];

export const TargetManagement = () => {
  const [targets, setTargets] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    month: new Date().getMonth() + 1,
    year: currentYear,
  });

  const [isTargetModalOpen, setIsTargetModalOpen] = useState(false);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedTarget, setSelectedTarget] = useState(null);
  const [editingTarget, setEditingTarget] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const [targetForm] = Form.useForm();
  const [assignForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const [saving, setSaving] = useState(false);

  // ── Bulk upload state ─────────────────────────────────────────────────────
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadReport, setUploadReport] = useState("");

  const token = JSON.parse(sessionStorage.getItem("user"))?.accessToken;
  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  const fetchTargets = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.month) params.append("month", filters.month);
      if (filters.year) params.append("year", filters.year);
      const res = await axios.get(`${Api_Url}api/v1/Target?${params.toString()}`, authHeader);
      setTargets(res?.data?.data || []);
    } catch (e) {
      ErrorToast("Failed to fetch targets");
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const res = await axios.get(
        `${Api_Url}api/v1/UserProfile?PageNumber=1&PageSize=1000`,
        authHeader
      );
      setEmployees(res?.data?.data || []);
    } catch (e) {
      // silent — employee dropdown just stays empty
    }
  };

  useEffect(() => {
    fetchTargets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => {
    fetchEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const roleLabel = (r) => ROLE_OPTIONS.find((x) => x.value === r)?.label || "-";
  const typeLabel = (t) => TYPE_OPTIONS.find((x) => x.value === t)?.label || "-";

  // --- Create target ---
  const submitTarget = async (values) => {
    setSaving(true);
    try {
      const payload = {
        Type: values.Type,
        Role: values.Type === 1 ? null : values.Role,
        Name: values.Name?.trim(),
        Month: values.Month,
        Year: values.Year,
      };
      const res = await axios.post(`${Api_Url}api/v1/Target`, payload, authHeader);
      if (res.status === 200 || res.status === 201) {
        message.success("Target created");
        setIsTargetModalOpen(false);
        targetForm.resetFields();
        fetchTargets();
      }
    } catch (e) {
      message.error(e.response?.data?.message || "Failed to create target");
    } finally {
      setSaving(false);
    }
  };

  // --- Open edit modal pre-filled ---
  const openEdit = (record) => {
    setEditingTarget(record);
    editForm.setFieldsValue({
      Type: record.type,
      Role: record.role ?? undefined,
      Name: record.name,
      Month: record.month,
      Year: record.year,
    });
    setIsEditModalOpen(true);
  };

  // --- Submit update ---
  const submitEdit = async (values) => {
    setSaving(true);
    try {
      const payload = {
        Id: editingTarget.id,
        Type: values.Type,
        Role: values.Type === 1 ? null : values.Role,
        Name: values.Name?.trim(),
        Month: values.Month,
        Year: values.Year,
      };
      const res = await axios.put(`${Api_Url}api/v1/Target/${editingTarget.id}`, payload, authHeader);
      if (res.status === 200 || res.status === 201) {
        message.success("Target updated");
        setIsEditModalOpen(false);
        editForm.resetFields();
        setEditingTarget(null);
        fetchTargets();
      }
    } catch (e) {
      message.error(e.response?.data?.message || "Failed to update target");
    } finally {
      setSaving(false);
    }
  };

  // --- Delete ---
  const handleDelete = async (id) => {
    setDeletingId(id);
    try {
      await axios.delete(`${Api_Url}api/v1/Target/${id}`, authHeader);
      message.success("Target deleted");
      fetchTargets();
    } catch (e) {
      message.error(e.response?.data?.message || "Failed to delete target");
    } finally {
      setDeletingId(null);
    }
  };

  // --- Assign employee target ---
  const openAssign = (target) => {
    setSelectedTarget(target);
    assignForm.resetFields();
    // Operations targets are always 100 — pre-fill and lock the value
    if (target.role === 3) {
      assignForm.setFieldsValue({ TargetValue: 100 });
    }
    setIsAssignModalOpen(true);
  };

  const submitAssign = async (values) => {
    setSaving(true);
    try {
      const payload = {
        UserProfileId: values.UserProfileId,
        TargetId: selectedTarget.id,
        TargetValue: values.TargetValue || 0,
        AchievementValue: values.AchievementValue || 0,
        OpeningValue: values.OpeningValue || 0,
      };
      const res = await axios.post(`${Api_Url}api/v1/EmployeeTarget`, payload, authHeader);
      if (res.status === 200 || res.status === 201) {
        message.success("Employee target assigned");
        setIsAssignModalOpen(false);
        assignForm.resetFields();
      }
    } catch (e) {
      message.error(e.response?.data?.message || "Failed to assign employee target");
    } finally {
      setSaving(false);
    }
  };

  // ── Bulk upload handler ───────────────────────────────────────────────────
  const handleUpload = async () => {
    if (!uploadFile) {
      message.warning("Please choose an Excel or CSV file first.");
      return;
    }
    setUploading(true);
    setUploadReport("");
    try {
      const formData = new FormData();
      formData.append("ExcelFile", uploadFile);
      formData.append("Month", filters.month);
      formData.append("Year", filters.year);
      const res = await axios.post(
        `${Api_Url}api/v1/EmployeeTarget/upload-excel`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      SuccessToast(res?.data?.message || "Upload complete");
      setUploadReport(res?.data?.data || "");
      setUploadFile(null);
      fetchTargets();
    } catch (e) {
      ErrorToast(e.response?.data?.message || "Upload failed");
      if (e.response?.data?.data) setUploadReport(e.response.data.data);
    } finally {
      setUploading(false);
    }
  };

  // ── Download CSV template ─────────────────────────────────────────────────
  const downloadTemplate = () => {
    const headers = ["Employee Code", "Target Name", "Target Value", "Achievement Value", "Opening Value"];
    const sample  = ["EMP001", "Bouncing", "5", "0", "12.5"];
    const csv = headers.join(",") + "\n" + sample.join(",") + "\n";
    const blob = new Blob([csv], { type: "text/csv" });
    const url  = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href  = url;
    link.setAttribute("download", "EmployeeTarget_Template.csv");
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const columns = [
    { title: "ID", dataIndex: "id", width: 70, sorter: (a, b) => a.id - b.id, defaultSortOrder: "ascend" },
    { title: "Target Name", dataIndex: "name", sorter: (a, b) => (a.name || "").localeCompare(b.name || "") },
    { title: "Type", dataIndex: "type", render: (t) => typeLabel(t), sorter: (a, b) => (a.type || 0) - (b.type || 0) },
    { title: "Role", dataIndex: "role", render: (r) => (r ? roleLabel(r) : "-"), sorter: (a, b) => (a.role || 0) - (b.role || 0) },
    { title: "Month", dataIndex: "month", render: (m) => MONTHS[m - 1] || m, sorter: (a, b) => (a.month || 0) - (b.month || 0) },
    { title: "Year", dataIndex: "year", sorter: (a, b) => (a.year || 0) - (b.year || 0) },
    {
      title: "Action",
      key: "action",
      width: 260,
      render: (_, record) => (
        <Space size="small" wrap>
          {record.type === 2 && (
            <Button type="primary" ghost size="small" onClick={() => openAssign(record)}>
              Assign to Employee
            </Button>
          )}
          {record.type === 1 && (
            <span className="text-muted">Branch target</span>
          )}
          <Tooltip title="Edit">
            <Button
              size="small"
              onClick={() => openEdit(record)}
            >
              <i className="fa-solid fa-pen-to-square" />
            </Button>
          </Tooltip>
          <Tooltip title="Delete">
            <Popconfirm
              title="Delete this target?"
              description="This action cannot be undone."
              onConfirm={() => handleDelete(record.id)}
              okText="Yes, delete"
              cancelText="Cancel"
              okButtonProps={{ danger: true }}
            >
              <Button
                size="small"
                danger
                loading={deletingId === record.id}
              >
                {deletingId !== record.id && <i className="fa-solid fa-trash" />}
              </Button>
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  const showOpeningValue = (() => {
    const n = (selectedTarget?.name || "").toLowerCase();
    return n.includes("bounc") || n.includes("delinq") || n.includes("deliq");
  })();

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <div className="row mb-3">
          <div className="col-12 d-flex align-items-center justify-content-between">
            <h3 className="mb-0">Target Management</h3>
            <button className="btn btn-primary" onClick={() => setIsTargetModalOpen(true)}>
              <i className="fa-solid fa-plus me-2"></i> Add Target
            </button>
          </div>
        </div>

        <Card className="mb-4">
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={6}>
              <Select
                placeholder="Month"
                value={filters.month}
                onChange={(v) => setFilters((p) => ({ ...p, month: v }))}
                style={{ width: "100%" }}
              >
                {MONTHS.map((m, i) => (
                  <Option key={i + 1} value={i + 1}>{m}</Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Select
                placeholder="Year"
                value={filters.year}
                onChange={(v) => setFilters((p) => ({ ...p, year: v }))}
                style={{ width: "100%" }}
              >
                {YEARS.map((y) => (
                  <Option key={y} value={y}>{y}</Option>
                ))}
              </Select>
            </Col>
          </Row>
        </Card>

        {/* ── Bulk Upload card ── */}
        <Card className="mb-4" size="small" title="Bulk Upload Employee Targets">
          <div className="d-flex flex-wrap gap-2 align-items-center">
            <Select
              value={filters.month}
              onChange={(v) => setFilters((p) => ({ ...p, month: v }))}
              style={{ width: 140 }}
            >
              {MONTHS.map((m, i) => (<Option key={i + 1} value={i + 1}>{m}</Option>))}
            </Select>
            <Select
              value={filters.year}
              onChange={(v) => setFilters((p) => ({ ...p, year: v }))}
              style={{ width: 100 }}
            >
              {YEARS.map((y) => (<Option key={y} value={y}>{y}</Option>))}
            </Select>
            <Upload
              beforeUpload={(f) => { setUploadFile(f); return false; }}
              maxCount={1}
              fileList={uploadFile ? [uploadFile] : []}
              onRemove={() => setUploadFile(null)}
              accept=".xlsx,.xls,.csv"
            >
              <Button icon={<UploadOutlined />}>Choose File</Button>
            </Upload>
            <Button type="primary" loading={uploading} onClick={handleUpload}>
              Upload
            </Button>
            <Button onClick={downloadTemplate}>Download Template</Button>
          </div>
          <Alert
            className="mt-3"
            type="info"
            showIcon
            message={
              <>
                Columns: <b>Employee Code | Target Name | Target Value | Achievement Value | Opening Value</b>.
                The Target must already exist (Employee type) for the selected month/year.
                <b> Opening Value</b> applies to Bouncing and Delinquency targets.
                Existing employee + target rows are updated. Accepts .csv, .xlsx, .xls
              </>
            }
          />
          {uploadReport && (
            <pre style={{ whiteSpace: "pre-wrap", marginTop: 12, marginBottom: 0, maxHeight: 220, overflow: "auto" }}>
              {uploadReport}
            </pre>
          )}
        </Card>

        <div className="table-responsive">
          <Table
            className="table-striped"
            columns={columns}
            dataSource={targets}
            rowKey="id"
            loading={loading}
            pagination={{ showSizeChanger: true, defaultPageSize: 10, pageSizeOptions: ["10", "20", "50", "100", "200"] }}
            scroll={{ x: 900 }}
          />
        </div>

        {/* ── Add Target modal ── */}
        <Modal
          title={<span style={{ fontSize: 18, fontWeight: "bold" }}>Add Target</span>}
          open={isTargetModalOpen}
          onCancel={() => setIsTargetModalOpen(false)}
          onOk={() => targetForm.submit()}
          okText="Save Target"
          confirmLoading={saving}
          centered
          width={640}
        >
          <Form form={targetForm} layout="vertical" onFinish={submitTarget}
            initialValues={{ Type: 2, Month: filters.month, Year: filters.year }}>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="Type" label="Type" rules={[{ required: true }]}>
                  <Select>
                    {TYPE_OPTIONS.map((t) => (
                      <Option key={t.value} value={t.value}>{t.label}</Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item noStyle shouldUpdate={(prev, cur) => prev.Type !== cur.Type}>
                  {({ getFieldValue }) =>
                    getFieldValue("Type") === 2 ? (
                      <Form.Item name="Role" label="Role" rules={[{ required: true, message: "Select a role" }]}>
                        <Select placeholder="Select role">
                          {ROLE_OPTIONS.map((r) => (
                            <Option key={r.value} value={r.value}>{r.label}</Option>
                          ))}
                        </Select>
                      </Form.Item>
                    ) : null
                  }
                </Form.Item>
              </Col>
            </Row>
            <Form.Item name="Name" label="Target Name" rules={[{ required: true, message: "Enter a target name" }]}>
              <Input placeholder="e.g. Disbursement Amount" />
            </Form.Item>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="Month" label="Month" rules={[{ required: true }]}>
                  <Select>
                    {MONTHS.map((m, i) => (
                      <Option key={i + 1} value={i + 1}>{m}</Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="Year" label="Year" rules={[{ required: true }]}>
                  <Select>
                    {YEARS.map((y) => (
                      <Option key={y} value={y}>{y}</Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Modal>

        {/* ── Edit Target modal ── */}
        <Modal
          title={<span style={{ fontSize: 18, fontWeight: "bold" }}>Edit Target</span>}
          open={isEditModalOpen}
          onCancel={() => { setIsEditModalOpen(false); editForm.resetFields(); setEditingTarget(null); }}
          onOk={() => editForm.submit()}
          okText="Save Changes"
          confirmLoading={saving}
          centered
          width={640}
        >
          <Form form={editForm} layout="vertical" onFinish={submitEdit}>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="Type" label="Type" rules={[{ required: true }]}>
                  <Select>
                    {TYPE_OPTIONS.map((t) => (
                      <Option key={t.value} value={t.value}>{t.label}</Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item noStyle shouldUpdate={(prev, cur) => prev.Type !== cur.Type}>
                  {({ getFieldValue }) =>
                    getFieldValue("Type") === 2 ? (
                      <Form.Item name="Role" label="Role" rules={[{ required: true, message: "Select a role" }]}>
                        <Select placeholder="Select role">
                          {ROLE_OPTIONS.map((r) => (
                            <Option key={r.value} value={r.value}>{r.label}</Option>
                          ))}
                        </Select>
                      </Form.Item>
                    ) : null
                  }
                </Form.Item>
              </Col>
            </Row>
            <Form.Item name="Name" label="Target Name" rules={[{ required: true, message: "Enter a target name" }]}>
              <Input />
            </Form.Item>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="Month" label="Month" rules={[{ required: true }]}>
                  <Select>
                    {MONTHS.map((m, i) => (
                      <Option key={i + 1} value={i + 1}>{m}</Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="Year" label="Year" rules={[{ required: true }]}>
                  <Select>
                    {YEARS.map((y) => (
                      <Option key={y} value={y}>{y}</Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Modal>

        {/* ── Assign employee target modal ── */}
        <Modal
          title={<span style={{ fontSize: 18, fontWeight: "bold" }}>Assign Employee Target</span>}
          open={isAssignModalOpen}
          onCancel={() => setIsAssignModalOpen(false)}
          onOk={() => assignForm.submit()}
          okText="Save"
          confirmLoading={saving}
          centered
          width={640}
        >
          <p className="text-muted">
            Target: <b>{selectedTarget?.name}</b> ({selectedTarget && MONTHS[selectedTarget.month - 1]} {selectedTarget?.year})
          </p>
          <Form form={assignForm} layout="vertical" onFinish={submitAssign}>
            <Form.Item name="UserProfileId" label="Employee" rules={[{ required: true, message: "Select an employee" }]}>
              <Select
                showSearch
                placeholder="Search employee"
                optionFilterProp="children"
                filterOption={(input, option) =>
                  (option.children || "").toLowerCase().includes(input.toLowerCase())
                }
              >
                {employees.map((e) => (
                  <Option key={e.id} value={e.id}>
                    {(e.firstName || "") + " " + (e.lastName || "") + (e.code ? ` (${e.code})` : "")}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="TargetValue" label="Target Value" rules={[{ required: true }]}>
                  {selectedTarget?.role === 3 ? (
                    // Operations targets are always 100 — show locked display
                    <InputNumber
                      min={0}
                      style={{ width: "100%", background: "#f5f5f5" }}
                      disabled
                    />
                  ) : (
                    <InputNumber min={0} style={{ width: "100%" }} />
                  )}
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="AchievementValue" label="Achievement Value">
                  <InputNumber min={0} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
            </Row>
            {showOpeningValue && (
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="OpeningValue" label="Opening Value">
                    <InputNumber min={0} style={{ width: "100%" }} />
                  </Form.Item>
                </Col>
              </Row>
            )}
          </Form>
        </Modal>
      </div>
    </div>
  );
};
